/**
 * TierTwoMappingsOverlay (DC-1569 #2)
 *
 * Drill-down overlay surfacing the per-column inferred mappings that fed a
 * Tier-2 I2/I4 PASS. Reached via the "View inferred mappings →" link rendered
 * under the rule's evidence body when the IPillarTierBadge shows "Inferred."
 *
 * Each row shows column_name + target_iri + confidence, with a one-click
 * "Copy UC tag IRI" helper so a user can paste it straight into Databricks
 * Unity Catalog column tags to upgrade that mapping from Tier 2 (inferred) to
 * Tier 1 (declared).
 *
 * Data source: GET /api/journey/:group_id/i-pillar-inferred-mappings
 * Spec: Documentation/Development/Design Docs/Sprint 16/Triangulated_I_Pillar_Design.md
 */

import React, { useEffect, useState, useCallback } from 'react';
import { X, Copy, Check, AlertCircle, Loader2 } from 'lucide-react';
import { useApiClient } from '../../hooks/useApiClient';
import { useJourneyId } from '../../contexts/JourneyContext';

// ============================================================================
// TYPES
// ============================================================================

export interface InferredMapping {
  column_name: string;
  target_iri: string;
  confidence: number;
  source_ontology_id: string | null;
}

export interface InferredMappingsResponse {
  result_id: string | null;
  target_ontology_id: string | null;
  total_columns: number;
  mapped_columns: number;
  coverage_pct: number;
  confidence_avg: number | null;
  completed_at: string | null;
  mappings: InferredMapping[];
}

interface TierTwoMappingsOverlayProps {
  ruleId: 'I2' | 'I4';
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const TierTwoMappingsOverlay: React.FC<TierTwoMappingsOverlayProps> = ({
  ruleId,
  isOpen,
  onClose,
}) => {
  const journeyId = useJourneyId();
  const { authFetch } = useApiClient();

  const [data, setData] = useState<InferredMappingsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedIri, setCopiedIri] = useState<string | null>(null);

  // Fetch mappings whenever the overlay opens.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await authFetch(
          `/api/journey/${journeyId}/i-pillar-inferred-mappings?limit=50`,
        );
        if (!res.ok) {
          throw new Error(`Failed to load mappings: ${res.statusText}`);
        }
        const payload = (await res.json()) as InferredMappingsResponse;
        if (!cancelled) setData(payload);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load mappings');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, journeyId, authFetch]);

  // Escape closes the overlay.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const handleCopy = useCallback(async (iri: string) => {
    try {
      await navigator.clipboard.writeText(iri);
      setCopiedIri(iri);
      setTimeout(() => {
        setCopiedIri((current) => (current === iri ? null : current));
      }, 1500);
    } catch {
      // Clipboard API can fail in insecure contexts; fall back gracefully.
      setCopiedIri(null);
    }
  }, []);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="tier-two-mappings-title"
      style={backdropStyle}
      onClick={onClose}
    >
      <div
        style={panelStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <header style={headerStyle}>
          <div>
            <h2 id="tier-two-mappings-title" style={{ margin: 0, fontSize: '1.1rem', color: 'var(--tri-ink)' }}>
              Tier-2 inferred mappings — {ruleId}
            </h2>
            <p style={subheadingStyle}>
              Mappings the Ontology Inference run produced against the journey’s target ontology.
              Tag any of these columns in Unity Catalog with the matching IRI to upgrade
              to Tier 1 (Verified).
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close mappings overlay"
            style={closeButtonStyle}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        {loading && (
          <div style={statusStyle} role="status" aria-live="polite">
            <Loader2 size={16} aria-hidden="true" />
            <span>Loading inferred mappings…</span>
          </div>
        )}

        {error && (
          <div style={errorStyle} role="alert">
            <AlertCircle size={16} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && data && (
          <>
            <div style={summaryStyle}>
              {data.result_id === null ? (
                <span>
                  No inference run found{' '}
                  {data.target_ontology_id
                    ? 'for this journey × target ontology pair yet.'
                    : '— set an I-pillar target ontology in journey Settings to enable Tier 2 scoring.'}
                </span>
              ) : (
                <span>
                  <strong>
                    {data.mapped_columns} of {data.total_columns} columns
                  </strong>{' '}
                  mapped ({Math.round(data.coverage_pct)}% coverage)
                  {data.confidence_avg !== null && (
                    <> · average confidence{' '}
                      <strong>{(data.confidence_avg * 100).toFixed(1)}%</strong>
                    </>
                  )}
                </span>
              )}
            </div>

            {data.mappings.length > 0 && (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Column</th>
                    <th style={thStyle}>Mapped IRI</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Confidence</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>{/* copy column */}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.mappings.map((m) => (
                    <tr key={`${m.column_name}::${m.target_iri}`}>
                      <td style={tdColStyle}>{m.column_name}</td>
                      <td style={tdIriStyle}>{m.target_iri}</td>
                      <td style={tdConfStyle}>{(m.confidence * 100).toFixed(1)}%</td>
                      <td style={tdActionStyle}>
                        <button
                          type="button"
                          onClick={() => handleCopy(m.target_iri)}
                          aria-label={`Copy UC tag IRI for ${m.column_name}`}
                          style={copyButtonStyle}
                        >
                          {copiedIri === m.target_iri ? (
                            <>
                              <Check size={14} aria-hidden="true" />
                              <span>Copied</span>
                            </>
                          ) : (
                            <>
                              <Copy size={14} aria-hidden="true" />
                              <span>Copy IRI</span>
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// STYLES — inline, matches the dark-theme conventions of EvaluateRuleCard
// ============================================================================

const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(2, 6, 23, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '2rem',
};

const panelStyle: React.CSSProperties = {
  background: 'var(--tri-card)',
  color: 'var(--tri-ink)',
  border: '1px solid var(--tri-taupe)',
  borderRadius: 12,
  padding: '1.25rem',
  maxWidth: 920,
  width: '100%',
  maxHeight: '85vh',
  overflowY: 'auto',
  boxShadow: '0 24px 64px rgba(0, 0, 0, 0.5)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '1rem',
  marginBottom: '1rem',
};

const subheadingStyle: React.CSSProperties = {
  margin: '0.375rem 0 0 0',
  fontSize: '0.8125rem',
  color: 'var(--tri-slate)',
  lineHeight: 1.4,
  maxWidth: '64ch',
};

const closeButtonStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--tri-taupe)',
  borderRadius: 6,
  color: 'var(--tri-body)',
  padding: '0.25rem 0.5rem',
  cursor: 'pointer',
};

const statusStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '1rem',
  color: 'var(--tri-slate)',
};

const errorStyle: React.CSSProperties = {
  ...statusStyle,
  color: 'var(--tri-hard)',
};

const summaryStyle: React.CSSProperties = {
  padding: '0.625rem 0.75rem',
  background: 'var(--tri-card-2)',
  borderRadius: 8,
  marginBottom: '0.75rem',
  fontSize: '0.875rem',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.8125rem',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.625rem',
  borderBottom: '1px solid var(--tri-taupe)',
  color: 'var(--tri-slate)',
  fontWeight: 600,
};

const tdColStyle: React.CSSProperties = {
  padding: '0.5rem 0.625rem',
  borderBottom: '1px solid var(--tri-taupe)',
  fontFamily: 'JetBrains Mono, Consolas, monospace',
  color: 'var(--tri-ink)',
};

const tdIriStyle: React.CSSProperties = {
  ...tdColStyle,
  color: 'var(--tri-teal)',
  wordBreak: 'break-all',
};

const tdConfStyle: React.CSSProperties = {
  padding: '0.5rem 0.625rem',
  borderBottom: '1px solid var(--tri-taupe)',
  textAlign: 'right',
  color: 'var(--tri-body)',
};

const tdActionStyle: React.CSSProperties = {
  padding: '0.5rem 0.625rem',
  borderBottom: '1px solid var(--tri-taupe)',
  textAlign: 'right',
};

const copyButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
  padding: '0.25rem 0.625rem',
  background: 'var(--tri-neutral-bg)',
  color: 'var(--tri-neutral)',
  border: '1px solid var(--tri-neutral)',
  borderRadius: 9999,
  fontSize: '0.72rem',
  fontWeight: 600,
  cursor: 'pointer',
};
